import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Paper,
  Tooltip
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import SendIcon from '@mui/icons-material/Send';
import CancelIcon from '@mui/icons-material/Cancel';
import { useOrganization } from '../../context/OrganizationContext';
import { useOrganizationAccess } from '../../hooks/useOrganizationAccess';
import { useAuthToken } from '../../utils/accessToken';
import OrganizationLayout from '../../components/organization/OrganizationLayout';
import RoleAssignmentDialog from '../../components/organization/RoleAssignmentDialog';
import InviteMemberDialog from '../../components/organization/InviteMemberDialog';
import styles from '../../styles/TeamManagement.module.css';

/**
 * TeamManagement Page
 *
 * Displays and manages organization team members.
 *
 * Features:
 * - View all team members with roles and status
 * - Search/filter members
 * - Invite new members (owners/admins only)
 * - Edit member roles (owners/admins only)
 * - Remove members (owners only)
 * - Display member count vs plan limits
 */
function TeamManagement() {
  const { currentOrganization, userRole, refreshContext } = useOrganization();
  const { hasWriteAccess } = useOrganizationAccess();
  const token = useAuthToken();

  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);

  // Permission checks
  const canInvite = hasWriteAccess && (userRole === 'owner' || userRole === 'admin');
  const canEditRoles = hasWriteAccess && (userRole === 'owner' || userRole === 'admin');
  const canRemove = hasWriteAccess && userRole === 'owner';

  // Fetch team members
  useEffect(() => {
    if (currentOrganization?.organizationId && token) {
      fetchTeamMembers();
    }
  }, [currentOrganization?.organizationUuid, token]);

  // Filter members based on search
  useEffect(() => {
    if (!searchTerm) {
      setFilteredMembers(members);
    } else {
      const filtered = members.filter(member =>
        member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.role?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredMembers(filtered);
    }
  }, [searchTerm, members]);

  const fetchTeamMembers = async () => {
    if (!currentOrganization?.organizationId || !token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/members`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );


      // Get response as text first to debug
      const responseText = await response.text();

      if (!response.ok) {
        let errorMessage = 'Failed to fetch team members';
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          errorMessage = `Server error: ${response.status} - ${responseText.substring(0, 100)}`;
        }
        throw new Error(errorMessage);
      }

      const data = JSON.parse(responseText);

      // Transform backend data to match frontend format
      const transformedMembers = data.members.map(member => ({
        id: member.id,
        userId: member.userId,
        name: member.user ? `${member.user.firstName} ${member.user.lastName}` : 'Pending',
        email: member.user ? member.user.email : member.inviteeEmail,
        role: member.role.toLowerCase(),
        status: member.status.toLowerCase(),
        joinedAt: member.joinedAt || member.createdAt
      }));

      setMembers(transformedMembers);
      setFilteredMembers(transformedMembers);
    } catch (err) {
      setError('Failed to load team members. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInviteMember = () => {
    setInviteDialogOpen(true);
  };

  const handleSendInvites = async (invitations) => {
    try {
      setError('');

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/invitations`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ invitations })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send invitations');
      }

      const data = await response.json();

      // Refresh member list from server
      await fetchTeamMembers();

      // Refresh organization context to update member count in Quick Info
      refreshContext();
    } catch (err) {
      throw new Error(err.message || 'Failed to send invitations. Please try again.');
    }
  };

  const handleCloseInviteDialog = () => {
    setInviteDialogOpen(false);
  };

  const handleEditRole = (memberId) => {
    const member = members.find(m => m.id === memberId);
    if (member) {
      setSelectedMember(member);
      setRoleDialogOpen(true);
    }
  };

  const handleSaveRole = async (memberId, newRole) => {
    try {
      setError('');

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/members/${memberId}/role`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ role: newRole })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update member role');
      }

      const data = await response.json();

      // Refresh member list from server
      await fetchTeamMembers();

      // Refresh organization context to update member count in Quick Info
      refreshContext();
    } catch (err) {
      throw new Error(err.message || 'Failed to update role. Please try again.');
    }
  };

  const handleCloseRoleDialog = () => {
    setRoleDialogOpen(false);
    setSelectedMember(null);
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this member from the organization?')) {
      return;
    }

    try {
      setError('');

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/members/${memberId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove member');
      }


      // Refresh member list
      await fetchTeamMembers();

      // Refresh organization context to update member count in Quick Info
      refreshContext();
    } catch (err) {
      setError(err.message || 'Failed to remove member. Please try again.');
    }
  };

  const handleResendInvitation = async (memberId) => {
    try {
      setError('');

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/invitations/${memberId}/resend`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to resend invitation');
      }

      const data = await response.json();

      // Show success message
      alert('Invitation resent successfully!');
    } catch (err) {
      setError(err.message || 'Failed to resend invitation. Please try again.');
    }
  };

  const handleCancelInvitation = async (memberId) => {
    if (!window.confirm('Are you sure you want to cancel this invitation?')) {
      return;
    }

    try {
      setError('');

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/invitations/${memberId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cancel invitation');
      }


      // Refresh member list
      await fetchTeamMembers();

      // Refresh organization context to update member count in Quick Info
      refreshContext();
    } catch (err) {
      setError(err.message || 'Failed to cancel invitation. Please try again.');
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'owner':
        return 'error';
      case 'admin':
        return 'warning';
      case 'manager':
        return 'info';
      case 'pilot':
        return 'success';
      case 'viewer':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'invited':
      case 'pending':
        return 'warning';
      case 'inactive':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const memberLimit = currentOrganization?.subscription?.limits?.maxUsers || 5;
  const currentMemberCount = members.filter(m => m.status === 'active').length;
  const isAtLimit = currentMemberCount >= memberLimit;

  return (
    <OrganizationLayout>
      <div className={styles.container}>
        {/* Page Header */}
        <Box className={styles.header}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <PeopleIcon sx={{ fontSize: 40, color: '#1976d2' }} />
              <Box>
                <Typography variant="h4" component="h1">
                  Team Members
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                  Manage your organization's team members and roles
                </Typography>
              </Box>
            </Box>
            {canInvite && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<PersonAddIcon />}
                onClick={handleInviteMember}
                disabled={!hasWriteAccess || isAtLimit}
              >
                Invite Member
              </Button>
            )}
          </Box>
        </Box>

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Member Limit Warning */}
        {isAtLimit && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You've reached your plan's member limit ({memberLimit} members). Upgrade your plan to add more team members.
          </Alert>
        )}

        {/* Member Count Card */}
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Team Members
                </Typography>
                <Typography variant="h3" color="primary">
                  {currentMemberCount} / {memberLimit}
                </Typography>
              </Box>
              <Box textAlign="right">
                <Typography variant="body2" color="textSecondary">
                  Pending Invitations
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {members.filter(m => m.status === 'pending' || m.status === 'invited').length}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Search Bar */}
        <Card elevation={2} sx={{ mb: 2 }}>
          <CardContent>
            <TextField
              fullWidth
              placeholder="Search members by name, email, or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </CardContent>
        </Card>

        {/* Members Table */}
        <Card elevation={2}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Team Members ({filteredMembers.length})
            </Typography>

            {loading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : filteredMembers.length === 0 ? (
              <Box textAlign="center" p={4}>
                <Typography color="textSecondary">
                  {searchTerm ? 'No members found matching your search.' : 'No team members yet.'}
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Joined</TableCell>
                      {(canEditRoles || canRemove) && <TableCell align="right">Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredMembers.map((member) => (
                      <TableRow key={member.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {member.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {member.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={member.role.toUpperCase()}
                            color={getRoleColor(member.role)}
                            size="small"
                            sx={{ borderRadius: '4px' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={member.status.charAt(0).toUpperCase() + member.status.slice(1)}
                            color={getStatusColor(member.status)}
                            size="small"
                            variant="outlined"
                            sx={{ borderRadius: '4px' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {formatDate(member.joinedAt)}
                          </Typography>
                        </TableCell>
                        {(canEditRoles || canRemove) && (
                          <TableCell align="right">
                            <Box display="flex" gap={1} justifyContent="flex-end">
                              {(member.status === 'pending' || member.status === 'invited') ? (
                                // Actions for pending invitations
                                <>
                                  {canInvite && (
                                    <Tooltip title="Resend Invitation">
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => handleResendInvitation(member.id)}
                                        disabled={!hasWriteAccess}
                                      >
                                        <SendIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {canInvite && (
                                    <Tooltip title="Cancel Invitation">
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleCancelInvitation(member.id)}
                                        disabled={!hasWriteAccess}
                                      >
                                        <CancelIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </>
                              ) : (
                                // Actions for active members
                                <>
                                  {canEditRoles && member.role !== 'owner' && (
                                    <Tooltip title="Edit Role">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleEditRole(member.id)}
                                        disabled={!hasWriteAccess}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {canRemove && member.role !== 'owner' && (
                                    <Tooltip title="Remove Member">
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleRemoveMember(member.id)}
                                        disabled={!hasWriteAccess}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </>
                              )}
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Role Assignment Dialog */}
        <RoleAssignmentDialog
          open={roleDialogOpen}
          onClose={handleCloseRoleDialog}
          member={selectedMember}
          currentUserRole={userRole}
          onSave={handleSaveRole}
        />

        {/* Invite Member Dialog */}
        <InviteMemberDialog
          open={inviteDialogOpen}
          onClose={handleCloseInviteDialog}
          organizationName={currentOrganization?.organizationName || 'Organization'}
          currentUserRole={userRole}
          memberLimit={memberLimit}
          currentMemberCount={currentMemberCount}
          onSendInvites={handleSendInvites}
        />
      </div>
    </OrganizationLayout>
  );
}

export default TeamManagement;
